<script setup lang="ts">
  import { Text, IconRoundedSuccess, Tag, IconLocator, IconButton } from "@ui-kit";
  import { Image } from "@src/components";
  import { useStateActionList, type Good } from "./state";
  import OrderCardItemPickingHead from "./OrderCardItemPickingHead.vue";
  import OrderCardItemPickingColumn from "./OrderCardItemPickingColumn.vue";

  const { img_url, article, name, color, goods, isCompleted, zone } = defineProps<{
    img_url: string;
    name: string;
    article: string;
    color: string;
    goods: Good[];
    isCompleted: boolean;
    zone?: string;
  }>();
  const emit = defineEmits<{ (e: "click:locator"): void }>();

  const action_list_state = useStateActionList();
</script>

<template>
  <div class="order-card-item-picking">
    <div class="item">
      <div class="image-container">
        <div v-if="isCompleted" class="success-banner">
          <IconRoundedSuccess style="width: 32px; height: 23px" />
        </div>
        <Image :url="img_url" />
      </div>
      <div class="description">
        <IconButton class="order-card-item-picking__locator" @onclick="emit('click:locator')">
          <IconLocator />
        </IconButton>
        <Text font="body-medium-18" :wrap="false">{{ name }}</Text>
        <Text font="body-regular-14" :wrap="false">{{ article }}</Text>
        <Text font="body-regular-14" :wrap="false">{{ color }}</Text>
        <Tag v-if="zone" :text="zone" variant="black" />
      </div>
    </div>
    <div class="good-list">
      <OrderCardItemPickingHead />
      <OrderCardItemPickingColumn
        v-for="good in goods"
        :key="good.item.item_id"
        :title="good.item.size?.toUpperCase() ?? '-'"
        :isCollected="
          action_list_state.getCountActions({ itemId: good.item.item_id }) > 0 &&
          action_list_state.getCountActions({ itemId: good.item.item_id }) ===
            action_list_state.getCountActions({ itemId: good.item.item_id, status: 'done' })
        "
        :isCanceled="action_list_state.getCountActions({ itemId: good.item.item_id, status: 'cancelled' }) > 0"
        :countCollected="action_list_state.getCountActions({ itemId: good.item.item_id, status: 'done' })"
        :countTotal="action_list_state.getCountActions({ itemId: good.item.item_id })"
      />
    </div>
  </div>
</template>

<style scoped>
  .order-card-item-picking {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 12px;
    padding: 16px 16px 0;
    position: relative;
    min-width: 100%;
    max-width: 360px;

    .item {
      display: flex;
      gap: 16px;

      .image-container {
        position: relative;
      }

      .success-banner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--sf-base-background-loader);
      }

      .description {
        display: flex;
        flex-direction: column;
        gap: 6px;
        overflow: hidden;
        padding: 4px 0;
        flex: 1;
        position: relative;

        & :deep(.text) {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        & :deep(.text:first-of-type) {
          padding-right: 36px;
        }
      }

      & :deep(.image) {
        flex-shrink: 0;
      }

      & :deep(.image img) {
        height: 110px;
      }
    }

    .good-list {
      display: flex;
      border-radius: 16px;
      gap: 4px;
      flex: 1;
    }
  }

  .order-card-item-picking__locator {
    position: absolute;
    top: 0;
    right: 0;
    padding: 6px;
  }
</style>
